/**
 * Watchlist Store
 *
 * Uses saved_portfolios collection with portfolio_type='watchlist'.
 * Watchlists have symbols only (no shares/costBasis).
 * Clicking a symbol opens stock research in a new tab.
 */
import { create } from 'zustand';
import portfolioService from '../services/portfolioService';
import { auth } from '../firebase';
import { logger } from '../services/loggingService';
import { getErrorMessage, sanitizeSymbol } from '../utils/validation';
import analyticsService from '../services/analytics';

export interface WatchlistItem {
  symbol: string;
}

export interface SavedWatchlist {
  id: string;
  name: string;
  items: WatchlistItem[];
  createdAt: Date;
}

interface WatchlistState {
  watchlists: SavedWatchlist[];
  isLoading: boolean;
  error: string | null;
  abortController: AbortController | null;  // Prevents memory leaks from unmounted components
  loadWatchlists: (userId: string) => Promise<void>;
  saveWatchlist: (userId: string, name: string, items: WatchlistItem[]) => Promise<string | null>;
  renameWatchlist: (userId: string, watchlistId: string, newName: string) => Promise<void>;
  updateWatchlist: (userId: string, watchlistId: string, name: string, items: WatchlistItem[]) => Promise<void>;
  deleteWatchlist: (userId: string, watchlistId: string) => Promise<void>;
  getWatchlist: (watchlistId: string) => SavedWatchlist | undefined;
  addSymbolToWatchlist: (userId: string, watchlistId: string, symbol: string) => Promise<void>;
  removeSymbolFromWatchlist: (userId: string, watchlistId: string, symbol: string) => Promise<void>;
  cleanup: () => void;  // Cancel pending operations on unmount
}

const MAX_WATCHLISTS = 5;
const MAX_SYMBOLS = 100;

/* Watchlists are `saved_portfolios` rows typed `watchlist` ([[ROADMAP 1.24]]
 * step 3) — the SAME collection [[BUGS #155]] was about, and the last writer
 * still reaching Firestore directly. It mattered even with no rows yet: the
 * next watchlist created on staging would have been written into PRODUCTION's
 * portfolio collection, and the gateway (which dropped `portfolio_type`)
 * would then have listed it as an ordinary portfolio.
 *
 * Reads and writes moved together, per the 1.24 rule. */
const idToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    return user ? await user.getIdToken() : null;
  } catch {
    return null;
  }
};

export const useWatchlistStore = create<WatchlistState>((set, get) => ({
  watchlists: [],
  isLoading: false,
  error: null,
  abortController: null,

  loadWatchlists: async (userId: string) => {
    // Cancel previous request if still running
    const { abortController: oldController } = get();
    oldController?.abort();

    // Create new AbortController for this request
    const abortController = new AbortController();
    set({ abortController, isLoading: true, error: null });

    try {
      const token = await idToken();
      if (!token || !userId) { set({ isLoading: false }); return; }
      // `type=watchlist` — the gateway filters, so portfolios and watchlists
      // never bleed into each other's pages.
      const res = await portfolioService.listSavedPortfolios(token, 'watchlist');
      const snapshot = {
        docs: (res.portfolios || []).map((p) => ({
          id: p.id,
          data: () => ({ name: p.name, holdings: p.holdings, createdAt: p.createdAt }),
        })),
      };

      // Check if aborted before updating state
      if (abortController.signal.aborted) {
        return;
      }

      const watchlists = snapshot.docs.map(d => {
        const data = d.data();
        // Stored in portfolio shape; a watchlist reads only the symbols back out.
        // Written WITHOUT a quantity: the server refuses `shares: 0` ("must be a
        // positive number"), which made every add to a watchlist fail
        // ([[BUGS #522]]).
        const items = ((data.holdings as Array<{ symbol: string }> | undefined) || [])
          .map((h) => ({ symbol: h.symbol }));
        return {
          id: d.id,
          name: data.name || 'My Watchlist',
          items,
          // The gateway serializes createdAt as ISO text; an unparseable one
          // sorts to the epoch rather than rendering "Invalid Date".
          createdAt: (() => {
            const parsed = new Date(data.createdAt || 0);
            return isNaN(parsed.getTime()) ? new Date(0) : parsed;
          })(),
        } as SavedWatchlist;
      });
      set({ watchlists, isLoading: false });
    } catch (err) {
      // Silently ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      logger.error('Failed to load watchlists', { error: getErrorMessage(err) });
      set({ error: 'Failed to load watchlists', isLoading: false });
    }
  },

  saveWatchlist: async (userId: string, name: string, items: WatchlistItem[]) => {
    const { watchlists } = get();

    // Check limits
    if (watchlists.length >= MAX_WATCHLISTS) {
      set({ error: `Maximum ${MAX_WATCHLISTS} watchlists allowed. Delete one to add a new one.` });
      return null;
    }

    if (items.length > MAX_SYMBOLS) {
      set({ error: `Maximum ${MAX_SYMBOLS} symbols per watchlist allowed.` });
      return null;
    }

    // Sanitize all symbols before writing to Firestore
    const sanitizedItems = items.filter(item => sanitizeSymbol(item.symbol) !== null);
    // Refuse only when symbols were given and none is valid. An empty list is
    // how every watchlist starts — the page's "New Watchlist" saves one — and
    // refusing it made a watchlist impossible to create there ([[BUGS #522]]).
    if (items.length > 0 && sanitizedItems.length === 0) {
      set({ error: 'No valid symbols to save.' });
      return null;
    }

    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return null; }
      const created = await portfolioService.createSavedPortfolio(
        name.trim() || 'My Watchlist',
        sanitizedItems.map(item => ({ symbol: sanitizeSymbol(item.symbol)! })),
        token, null, 'watchlist');
      if (!created.success || !created.portfolio_id) {
        // A per-plan cap refusal carries the sentence to show ([[BUGS #24]]);
        // flattening it to a generic message would lose the upsell.
        set({ error: created.error || 'Failed to save watchlist', isLoading: false });
        return null;
      }
      const watchlistRef = { id: created.portfolio_id };

      // Reload watchlists
      await get().loadWatchlists(userId);
      logger.logCrud('create', 'watchlist', watchlistRef.id, true);
      analyticsService.trackWatchlistCreated(watchlistRef.id, items.length);
      // Activation: first watchlist with 5+ symbols = serious-user signal
      if (items.length >= 5) {
        analyticsService.trackActivation('first_watchlist_5plus', {
          watchlist_id: watchlistRef.id,
          symbol_count: items.length,
        });
      }
      return watchlistRef.id;
    } catch (err) {
      logger.logCrud('create', 'watchlist', '', false, getErrorMessage(err));
      set({ error: 'Failed to save watchlist', isLoading: false });
      return null;
    }
  },

  renameWatchlist: async (userId: string, watchlistId: string, newName: string) => {
    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return; }
      await portfolioService.updateSavedPortfolio(
        watchlistId, { name: newName.trim() || 'My Watchlist' }, token);
      set((state) => ({
        watchlists: state.watchlists.map(w =>
          w.id === watchlistId ? { ...w, name: newName.trim() || 'My Watchlist' } : w
        ),
        isLoading: false
      }));
      logger.logCrud('update', 'watchlist', watchlistId, true);
    } catch (err) {
      logger.logCrud('update', 'watchlist', watchlistId, false, getErrorMessage(err));
      set({ error: 'Failed to rename watchlist', isLoading: false });
    }
  },

  updateWatchlist: async (userId: string, watchlistId: string, name: string, items: WatchlistItem[]) => {
    if (items.length > MAX_SYMBOLS) {
      set({ error: `Maximum ${MAX_SYMBOLS} symbols per watchlist allowed.` });
      return;
    }

    // Sanitize symbols before writing to Firestore
    const sanitizedItems = items.filter(item => sanitizeSymbol(item.symbol) !== null);

    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return; }
      await portfolioService.updateSavedPortfolio(watchlistId, {
        name: name.trim() || 'My Watchlist',
        holdings: sanitizedItems.map(item => ({ symbol: sanitizeSymbol(item.symbol)! })),
      }, token);
      set((state) => ({
        watchlists: state.watchlists.map(w =>
          w.id === watchlistId ? { ...w, name: name.trim() || 'My Watchlist', items } : w
        ),
        isLoading: false
      }));
      logger.logCrud('update', 'watchlist', watchlistId, true);
    } catch (err) {
      logger.logCrud('update', 'watchlist', watchlistId, false, getErrorMessage(err));
      set({ error: 'Failed to update watchlist', isLoading: false });
    }
  },

  deleteWatchlist: async (userId: string, watchlistId: string) => {
    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return; }
      await portfolioService.deleteSavedPortfolio(watchlistId, token);
      set((state) => ({
        watchlists: state.watchlists.filter(w => w.id !== watchlistId),
        isLoading: false
      }));
      logger.logCrud('delete', 'watchlist', watchlistId, true);
      analyticsService.trackWatchlistDeleted(watchlistId);
    } catch (err) {
      logger.logCrud('delete', 'watchlist', watchlistId, false, getErrorMessage(err));
      set({ error: 'Failed to delete watchlist', isLoading: false });
    }
  },

  getWatchlist: (watchlistId: string) => {
    return get().watchlists.find(w => w.id === watchlistId);
  },

  addSymbolToWatchlist: async (userId: string, watchlistId: string, symbol: string) => {
    const watchlist = get().getWatchlist(watchlistId);
    if (!watchlist) {
      set({ error: 'Watchlist not found' });
      return;
    }

    const normalizedSymbol = symbol.toUpperCase().trim();

    // Check if already in watchlist
    if (watchlist.items.some(item => item.symbol === normalizedSymbol)) {
      set({ error: `${normalizedSymbol} is already in this watchlist` });
      return;
    }

    // Check limit
    if (watchlist.items.length >= MAX_SYMBOLS) {
      set({ error: `Maximum ${MAX_SYMBOLS} symbols per watchlist allowed` });
      return;
    }

    const updatedItems = [...watchlist.items, { symbol: normalizedSymbol }];
    await get().updateWatchlist(userId, watchlistId, watchlist.name, updatedItems);
    analyticsService.trackWatchlistSymbolAdded(watchlistId, normalizedSymbol);
  },

  removeSymbolFromWatchlist: async (userId: string, watchlistId: string, symbol: string) => {
    const watchlist = get().getWatchlist(watchlistId);
    if (!watchlist) {
      set({ error: 'Watchlist not found' });
      return;
    }

    const normalizedSymbol = symbol.toUpperCase().trim();
    const updatedItems = watchlist.items.filter(item => item.symbol !== normalizedSymbol);
    await get().updateWatchlist(userId, watchlistId, watchlist.name, updatedItems);
    analyticsService.trackWatchlistSymbolRemoved(watchlistId, normalizedSymbol);
  },

  /**
   * Cancel pending operations (call on component unmount to prevent memory leaks)
   */
  cleanup: () => {
    const { abortController } = get();
    abortController?.abort();
    set({ abortController: null });
  }
}));
